import React, { useState } from 'react';
import { Search, ChevronDown, ChevronUp, Package, CheckSquare, XSquare } from 'lucide-react';
// FT-221: Removed useCrew/resolveCrewName - name now stored directly in record.performerName

const MaintenanceHistoryTable = ({
  history = [],
  historySearch,
  setHistorySearch,
  formatDate,
  equipment = []
}) => {
  const [expandedRecord, setExpandedRecord] = useState(null);
  // FT-221: Removed useCrew() - no longer needed for name resolution

  // FT-221: Use performerName if present (saved at write time), else 'Not recorded'
  // Do NOT use resolveCrewName() - it may fail if crew list not loaded
  const getCrewDisplayName = (crewId, record = null) => {
    // FT-221: Check for pre-resolved performerName first (new records)
    if (record?.performerName) {
      return record.performerName;
    }
    // Legacy fallback: show 'Not recorded' (no resolver, no UUID display)
    return 'Not recorded';
  };
  // Filter history by search
  const filteredHistory = history.filter(record => {
    if (!historySearch) return true;
    const searchLower = historySearch.toLowerCase();
    // Check both canonical and legacy field names
    const inspectorName = record.performedBy || record.inspector || '';
    const recordDate = record.date || (record.completedAt ? new Date(record.completedAt).toISOString().split('T')[0] : '');
    return (
      inspectorName.toLowerCase().includes(searchLower) ||
      record.notes?.toLowerCase().includes(searchLower) ||
      formatDate(recordDate).toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      <div className="bg-slate-700 px-5 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Safety Check History</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search history..."
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-slate-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                Inspector
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                Result
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                Notes
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredHistory.length > 0 ? (
              filteredHistory.map((record, index) => {
                // Handle both canonical and legacy field names
                const recordDate = record.date || (record.completedAt ? new Date(record.completedAt).toISOString().split('T')[0] : '');
                const inspectorId = record.performedBy || record.inspector;
                // FT-221: Pass record for performerName check
                const inspectorName = getCrewDisplayName(inspectorId, record);
                const hasDefects = record.defectsFound || record.hasDefects || false;
                
                const isExpanded = expandedRecord === (record.id || index);
                const hasEquipmentChecks = record.equipmentChecks && Object.keys(record.equipmentChecks).length > 0;
                const hasSelectedEquipment = record.selectedEquipmentUnits && record.selectedEquipmentUnits.length > 0;
                const hasDetails = hasEquipmentChecks || hasSelectedEquipment || record.linkedDefectId;

                return (
                  <React.Fragment key={record.id || index}>
                    <tr
                      className={`hover:bg-gray-50 ${hasDetails ? 'cursor-pointer' : ''}`}
                      onClick={() => hasDetails && setExpandedRecord(isExpanded ? null : (record.id || index))}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          {hasDetails && (
                            <button className="mr-2">
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-500" />
                              )}
                            </button>
                          )}
                          {formatDate(recordDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {inspectorName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          hasDefects ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {hasDefects ? 'Defects Found' : 'Pass'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {record.notes || '-'}
                      </td>
                    </tr>
                    {isExpanded && hasDetails && (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-3">
                            {/* Equipment Checked */}
                            {hasSelectedEquipment && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Equipment Serviced:</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {record.selectedEquipmentUnits.map(equipmentId => {
                                    const eq = equipment.find(e => e.id === equipmentId);
                                    return (
                                      <div key={equipmentId} className="flex items-center text-sm text-gray-600">
                                        <Package className="h-4 w-4 mr-2 text-gray-400" />
                                        {eq ? `${eq.name} (${eq.location || 'No location'})` : equipmentId}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Detailed Equipment Checks */}
                            {hasEquipmentChecks && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Check Details:</h4>
                                <div className="space-y-1">
                                  {Object.entries(record.equipmentChecks).map(([equipmentId, check]) => {
                                    const eq = equipment.find(e => e.id === equipmentId);
                                    return (
                                      <div key={equipmentId} className="flex items-center text-sm">
                                        {check.status === 'pass' ? (
                                          <CheckSquare className="h-4 w-4 mr-2 text-green-600" />
                                        ) : (
                                          <XSquare className="h-4 w-4 mr-2 text-red-600" />
                                        )}
                                        <span className="text-gray-600">
                                          {eq ? eq.name : equipmentId}: {check.status}
                                          {check.notes && ` - ${check.notes}`}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Linked Defect */}
                            {record.linkedDefectId && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">Linked Defect:</h4>
                                <p className="text-sm text-blue-600">Defect ID: {record.linkedDefectId}</p>
                              </div>
                            )}

                            {/* All units checked indicator */}
                            {record.allUnitsChecked !== undefined && (
                              <div className="text-sm text-gray-600">
                                {record.allUnitsChecked ? '✓ All units were checked' : '⚠ Not all units were checked'}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                  {historySearch ? 'No matching history found' : 'No check history available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MaintenanceHistoryTable;